import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Box, Button, CircularProgress, Typography } from '@mui/material';
import { extractErrorMessage } from '../../utils/errorUtils';
import { useProbeProfilePhotoQuery, useSaveProfilePhotoMutation } from '../media/api/mediaProbeApi';
import { useAuthenticatedImage } from '../../hooks/useAuthenticatedImage';
import { buildProfilePhotoThumbnailUrl } from './profilePhotoThumbnailUrl';

type ProfilePhotoEditorProps = {
  relationId: number;
  initials?: string;
  onError?: (message: string) => void;
  uploadWithRelationId?: boolean;
  onUploaded?: () => void;
};

const ACCEPTED_IMAGE_TYPES = '.jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp';
const PROFILE_PHOTO_SIZE_PIXELS = 96;

export const ProfilePhotoEditor = ({
  relationId,
  initials = '',
  onError,
  uploadWithRelationId = true,
  onUploaded,
}: ProfilePhotoEditorProps) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFileName, setSelectedFileName] = useState<string | null>(null);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saveProfilePhoto, { isLoading: isSaving }] = useSaveProfilePhotoMutation();
  const profilePhotoQuery = useProbeProfilePhotoQuery(relationId, { skip: relationId <= 0 });
  const originalUrl = profilePhotoQuery.data?.resolvedUrl ?? null;
  const photoUrl = buildProfilePhotoThumbnailUrl(relationId, Boolean(originalUrl), originalUrl);
  const authenticatedPhotoUrl = useAuthenticatedImage(photoUrl);
  const hasPhoto = Boolean(authenticatedPhotoUrl) && !photoFailed;

  useEffect(() => {
    setPhotoFailed(false);
  }, [photoUrl]);

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedFileName(file.name);
    setError(null);
    try {
      await saveProfilePhoto(uploadWithRelationId ? { relationId, file } : { file }).unwrap();
      setPhotoFailed(false);
      onUploaded?.();
    } catch (requestError) {
      const message = extractErrorMessage(requestError, 'Failed to update profile photo.');
      if (onError) {
        onError(message);
      } else {
        setError(message);
      }
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <Box display="flex" flexDirection="column" alignItems="center" gap={1.5} mb={2}>
      <Avatar
        src={hasPhoto ? (authenticatedPhotoUrl ?? undefined) : undefined}
        imgProps={{ onError: () => setPhotoFailed(true) }}
        sx={{
          width: PROFILE_PHOTO_SIZE_PIXELS,
          height: PROFILE_PHOTO_SIZE_PIXELS,
          bgcolor: 'grey.200',
          color: 'text.secondary',
          fontSize: 30,
        }}
      >
        {initials}
      </Avatar>

      <Button component="label" variant="contained" disabled={isSaving}>
        {isSaving ? (
          <CircularProgress size={20} color="inherit" />
        ) : hasPhoto ? (
          'Change Photo'
        ) : (
          'Add Photo'
        )}
        <input
          ref={fileInputRef}
          hidden
          type="file"
          accept={ACCEPTED_IMAGE_TYPES}
          onChange={handleFileChange}
        />
      </Button>

      {selectedFileName && (
        <Typography variant="body2" color="text.secondary">
          {selectedFileName}
        </Typography>
      )}
      {error && <Alert severity="error">{error}</Alert>}
    </Box>
  );
};
